import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import Cropper, { type Area } from 'react-easy-crop'
import { Button } from '@/components/ui/button'
import { cropImageToBlob } from './crop-utils'

type CropDialogProps = {
  open: boolean
  title?: string
  imageUrl: string
  aspect: number
  saving?: boolean
  onClose: () => void
  onConfirm: (blob: Blob) => Promise<void> | void
}

export function CropDialog({
  open,
  title = '裁剪图片',
  imageUrl,
  aspect,
  saving = false,
  onClose,
  onConfirm,
}: CropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [area, setArea] = useState<Area | null>(null)
  const [working, setWorking] = useState(false)

  const busy = saving || working

  useEffect(() => {
    if (!open) return
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setArea(null)
  }, [open])

  // Esc 关闭（busy 时禁用）
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, busy, onClose])

  async function confirm() {
    if (!area || busy) return
    try {
      setWorking(true)

      // 输出 WebP 格式（更小体积、支持透明）
      const blob = await cropImageToBlob({
        imageUrl,
        croppedAreaPixels: area,
        mimeType: 'image/webp',
      })

      await onConfirm(blob)
    } finally {
      setWorking(false)
    }
  }

  if (!open) return null

  return (
    <div className='fixed inset-0 z-[60]'>
      <div
        className='absolute inset-0 bg-black/50'
        onClick={() => !busy && onClose()}
      />

      <div className='bg-background absolute top-1/2 left-1/2 w-[min(920px,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border shadow-lg'>
        <div className='flex items-center justify-between border-b px-4 py-3'>
          <div className='text-sm font-medium'>{title}</div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-9 w-9'
            onClick={() => !busy && onClose()}
            disabled={busy}
            aria-label='关闭'
            title='关闭'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>

        <div className='grid gap-4 p-4 md:grid-cols-[1fr_220px]'>
          <div className='bg-muted/20 relative h-[420px] overflow-hidden rounded-lg border'>
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_a, croppedAreaPixels) =>
                setArea(croppedAreaPixels)
              }
            />
          </div>

          <div className='space-y-3'>
            <div className='text-muted-foreground text-xs'>缩放</div>
            <input
              aria-label='缩放'
              type='range'
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className='w-full'
              disabled={busy}
            />

            <div className='pt-2' />

            <Button
              type='button'
              className='w-full'
              onClick={confirm}
              disabled={!area || busy}
            >
              {busy ? '处理中…' : '确认裁剪'}
            </Button>

            <Button
              type='button'
              variant='outline'
              className='w-full'
              onClick={() => !busy && onClose()}
              disabled={busy}
            >
              取消
            </Button>

            <div className='text-muted-foreground text-xs'>
              拖动调整位置，滑条缩放。
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
